// 결제 영구소유 개편(docs/결제_영구소유_개편_관리자화면_가이드.md) 표시 규칙 공용 헬퍼.
//
// 결제 이력 목록(PaymentSalesList)과 인보이스 모달(InvoiceDetailModal)이 같은 행을 그립니다.
// 신규 필드 해석이 두 곳에서 어긋나면 같은 결제가 화면마다 다르게 보이므로 여기 한 곳에만 둡니다.
//
// 하위호환: 관리자 화면은 백엔드보다 먼저 배포해도 안전해야 합니다(가이드 §5).
// 배포 전에는 purchase_type·grade·is_permanent 가 전부 undefined 입니다.
//   purchase_type undefined → 'new' 로 간주하되 뱃지는 찍지 않습니다(없는 값을 추정해 보여주지 않음)
//   is_permanent  undefined → 날짜를 그대로 표시하되 9999 센티널만 방어
//   is_permanent  === null  → plan 이 없는 결제(§3.2) → '—'

export const PERMANENT_TEXT = '영구'
export const NO_PLAN_TEXT = '—'

/**
 * 영구 센티널(9999-12-31) 판정.
 * new Date("9999-12-31") 은 UTC 자정으로 파싱되므로 KST 로컬에서 getFullYear() 가 9998 이 됩니다.
 * 타임존에 따라 판정이 뒤집히지 않도록 원문 문자열 앞 4자리로 봅니다.
 * "9999-12-31" 과 "9999-12-31T00:00:00" 둘 다 잡힙니다.
 */
export const isPermanentDateValue = (v) =>
  v !== null && v !== undefined && String(v).slice(0, 4) === '9999'

/** plan 이 없는 결제(앱스토어 경로의 basic 재구매 등). 기간 자체가 존재하지 않습니다. */
export const hasNoPlan = (row) => row?.is_permanent === null

/**
 * 영구 도서 열람권 여부.
 * is_permanent === false 가드가 있는 이유: 환불 건은 is_permanent=false + end_date=어제(§3.8) 라
 * 센티널 폴백이 발동할 일이 없지만, 플래그가 명시적으로 false 인 행을 문자열 폴백이 뒤집는 사고를 막습니다.
 */
export const isPermanentRow = (row) =>
  row?.is_permanent === true ||
  (row?.is_permanent !== false && isPermanentDateValue(row?.end_date))

/**
 * 종료일 표시 문자열. 화면마다 날짜 포맷이 달라 formatDate 를 콜백으로 받습니다.
 * @param {object} row paymentHistory items[] 의 한 행
 * @param {(s: string) => string} formatDate 호출부의 날짜 포맷터
 */
export const endDateText = (row, formatDate) => {
  if (hasNoPlan(row)) return NO_PLAN_TEXT
  if (isPermanentRow(row)) return PERMANENT_TEXT
  return formatDate(row?.end_date)
}

/** 값이 실제로 내려온 행에서만 true. 뱃지 노출 조건으로 씁니다. */
export const hasPurchaseType = (row) =>
  row?.purchase_type === 'new' || row?.purchase_type === 'upgrade'

/** 기간 라벨 같은 "로직"은 배포 전에도 종전대로 동작해야 하므로 new 로 폴백합니다. */
export const isUpgradeRow = (row) => row?.purchase_type === 'upgrade'

export const purchaseTypeLabel = (t) => (t === 'upgrade' ? '업그레이드' : '도서 구매')
export const purchaseTypePillKind = (t) => (t === 'upgrade' ? 'info' : 'default')
export const purchaseTypeIcon = (t) => (t === 'upgrade' ? 'upgrade' : 'menu_book')

const GRADE_LABELS = { basic: 'Basic', standard: 'Standard', premium: 'Premium' }
const GRADE_PILL = { basic: 'default', standard: 'info', premium: 'yellow' }

/** 알 수 없는 등급(혹은 미배포로 undefined)은 빈 문자열 → 호출부에서 뱃지를 그리지 않습니다. */
export const gradeLabel = (g) => GRADE_LABELS[g] || ''
export const gradePillKind = (g) => GRADE_PILL[g] || 'default'

/** 가격표 정렬용 등급 순위. basic < standard < premium */
export const GRADE_ORDER = { basic: 0, standard: 1, premium: 2 }

/** 무료/사은품 지급 건. 쿠폰을 쓴 결제가 아닙니다. */
export const isFreeGift = (row) => row?.payment_method === 'F'

/**
 * 화면에 그릴 쿠폰 할인액.
 * 과거 무료 지급분에는 옛 기본값(19.99)이 그대로 남아 있는데 백엔드 영역이라 값 자체는 고칠 수 없습니다.
 * 그대로 그리면 "정가를 할인해 줬다"로 읽히므로 무료 건은 0 으로 봅니다.
 */
export const visibleCouponAmount = (row) =>
  isFreeGift(row) ? 0 : Number(row?.coupon_amount) || 0

/**
 * 이 결제가 준 기능 이용권의 만료일 표기.
 *
 * feature_end_date 에는 9999-12-31 이 올 수 있습니다 — 개편 전 구매한 std/prem 은
 * 마이그레이션이 기능 만료일을 도서 행에서 복사해 기능도 영구(그랜드파더링)입니다.
 * 그래서 날짜만 보고 그리면 "기능 ~9999-12-31" 이 찍힙니다.
 * is_feature_permanent 플래그로 분기하고, 플래그가 없는 구버전 응답을 위해
 * 9999 센티널도 함께 막습니다.
 *
 * 반환값이 빈 문자열이면 이 결제에 기능 이용권이 없다는 뜻입니다(basic 구매 등).
 */
export const featureEndText = (row, formatDate) => {
  if (row?.is_feature_permanent === true) return PERMANENT_TEXT
  const end = row?.feature_end_date
  if (!end) return ''
  if (isPermanentDateValue(end)) return PERMANENT_TEXT
  return formatDate(end)
}

/**
 * 이용 기간 셀에 덧붙이는 부기. 예: "기능 영구" / "기능 ~2027-08-18"
 *
 * 도서 종료일과 값이 같으면 아무것도 반환하지 않습니다.
 * 마이그레이션이 기능 만료일을 book 행에서 복사해서 **기존 데이터는 대부분 두 값이 같습니다**
 * (백엔드 dev 실측: new + std/prem 458건 중 455건). 그대로 그리면 "영구 / 기능 영구" 처럼
 * 같은 내용이 한 번 더 찍힐 뿐입니다. 개편 후 새로 산 결제부터 두 값이 갈리므로,
 * **부기는 도서 기간이 알려주지 못하는 것이 있을 때만** 나옵니다.
 *
 * 원본 날짜가 필요한 곳(엑셀 등)은 featureEndText 를 직접 쓰세요 — 이 억제가 걸리지 않습니다.
 */
export const featurePeriodText = (row, formatDate) => {
  const text = featureEndText(row, formatDate)
  if (!text) return ''
  // 렌더된 문자열끼리 비교합니다. 원본 값 비교로는 "9999-12-31" 과 "9999-12-31T00:00:00" 처럼
  // 표기만 다르고 의미가 같은 쌍을 놓칩니다.
  if (text === endDateText(row, formatDate)) return ''
  return text === PERMANENT_TEXT ? `기능 ${PERMANENT_TEXT}` : `기능 ~${text}`
}

/**
 * 테스트 결제(샌드박스·QA) 여부.
 *
 * 종전에는 서버가 목록에서 아예 빼 버렸습니다. 그래서 "결제했는데 안 돼요" 문의에
 * 운영자가 검색해도 0건이라 "기록이 없습니다"라고 잘못 안내하는 일이 있었습니다.
 * 2026-08-25 부터 목록에 포함해 내려주므로, 숨기지 말고 **눈에 띄게 구분**만 합니다.
 *
 * === true 로 좁힌 이유: 백엔드 미배포·필드 누락 응답에서는 undefined 입니다.
 * 그 행을 테스트로도, 반대로 "실결제"로도 단정하지 않습니다(없는 정보를 지어내지 않음).
 */
export const isTestRow = (row) => row?.is_test === true

/* ────────────────────────────────────────────────────────────────
 * 환불 즉시 반영 (docs/환불_즉시반영_관리자화면_가이드.md)
 * ──────────────────────────────────────────────────────────────── */

/**
 * 환불 여부 판정.
 *
 * ⚠ grade 로 판정하면 안 됩니다. grade 는 "구매한 상품의 등급"이라 환불돼도 그대로입니다.
 * 앱팀이 이 값으로 "환불 미반영"을 오판정한 사례가 있어(가이드 §0) 판정 지점을 한 곳으로 모읍니다.
 */
export const isRefunded = (row) => !!row?.refund_at

/** 환불 즉시 반영(refundSync) 대상 결제수단. L 은 409, F 는 삭제 경로라 제외됩니다. */
const REFUND_SYNC_METHODS = ['A', 'G', 'T']

/**
 * "환불 즉시 반영" 버튼 노출 조건.
 *
 * refund_at 으로 더 좁히지 않는 이유: 이미 반영된 건은 200 + already_refunded 로 돌아오는
 * 정상 응답이고(멱등), 운영자가 "정말 반영돼 있나"를 확인하는 것도 이 버튼의 용도입니다.
 *
 * pay_code 는 좁힙니다. 스토어에 물어볼 코드가 없으면 서버가 409 로 거부할 수밖에 없어
 * 누를 수 있는 버튼으로 두면 안 됩니다(백엔드 회신 2026-08-31 — 서버 409 는 최종 방어선으로 유지).
 * 목록 응답에 pay_code 가 이미 있으므로 추가 조회 없이 판정합니다.
 * disabled 가 아니라 미노출인 이유: 비활성 버튼은 마우스 이벤트를 내지 않아 v-tooltip 이
 * 뜨지 않습니다 — 사유를 못 읽는 회색 버튼보다, 같은 열의 다른 액션들처럼 감추는 편이 낫습니다.
 */
export const canRefundSync = (row) =>
  REFUND_SYNC_METHODS.includes(row?.payment_method) && !!row?.pay_code

/**
 * "환불 실행" 버튼 노출 조건 — 우리 서버가 결제사에 취소를 거는 T/L 만 해당합니다.
 * A/G 는 사용자가 스토어에서 직접 환불하므로 실행 API 자체가 없습니다(가이드 §3.3).
 */
export const canRefundExec = (row) =>
  (row?.payment_method === 'T' || row?.payment_method === 'L') &&
  !!row?.pay_code &&
  !isRefunded(row)

/**
 * refundSync 응답을 화면 4분기로 접습니다.
 *
 *   applied  이번 호출로 반영함        → 초록 + 목록 새로고침
 *   already  호출 전에 이미 반영돼 있었음 → 정상(에러 아님)
 *   none     스토어가 "환불 아니다" 라고 답함
 *   unknown  판정 불가 — 조회 실패·자격증명 누락·조회 창 밖·테스트 결제
 *
 * ⚠ 'none' 은 **조회가 성공했을 때만** 나옵니다(store_lookup_ok === true).
 * 조회 실패를 "환불 아님"으로 뭉개면 운영자가 CS 를 잘못 종결하고 사용자는 환불받은 채로
 * 계속 열람하게 됩니다 — 가이드가 이번 작업에서 가장 중요하다고 못박은 지점입니다(§3.2).
 * 그래서 판정할 수 없는 모든 경우는 'none' 이 아니라 'unknown' 으로 떨어집니다.
 *
 * already 를 lookup 결과보다 먼저 보는 이유: 서버 DB 에 이미 refund_at 이 있으면
 * 스토어 조회가 실패했더라도 "이미 반영됨"이 사실이고 운영자에게 더 쓸모 있는 답입니다.
 */
export const refundSyncOutcome = (res) => {
  if (!res) return 'unknown'
  if (res.auto_processed === true) return 'applied'
  if (res.already_refunded === true) return 'already'
  if (res.store_lookup_ok === true && res.store_refunded === false) return 'none'
  return 'unknown'
}

const REFUND_SYNC_OUTCOME_META = {
  applied: { label: '반영 완료', pill: 'ok', icon: 'check_circle' },
  already: { label: '이미 반영됨', pill: 'info', icon: 'task_alt' },
  none: { label: '환불 내역 없음', pill: 'default', icon: 'do_not_disturb_on' },
  unknown: { label: '확인 불가', pill: 'default', icon: 'help_outline' },
}

export const refundSyncOutcomeMeta = (outcome) =>
  REFUND_SYNC_OUTCOME_META[outcome] || REFUND_SYNC_OUTCOME_META.unknown

/** lookup_source 원문은 서버 내부 용어라 그대로 노출하면 운영자가 읽을 수 없습니다. */
const LOOKUP_SOURCE_LABELS = {
  voided_list: 'Google 환불 목록',
  purchase_state: 'Google 구매 상태',
  apple_refund_lookup: 'Apple 환불 조회',
  toss_payment_lookup: 'Toss 결제 조회',
  unavailable: '조회 불가',
}

export const lookupSourceLabel = (s) => LOOKUP_SOURCE_LABELS[s] || s || '-'

/* ────────────────────────────────────────────────────────────────
 * 등급 업그레이드 잔여 가치 차감 (docs/결제_등급업그레이드_차감정책.md)
 * ──────────────────────────────────────────────────────────────── */

/** 오늘(로컬) YYYY-MM-DD. 날짜끼리 문자열로 비교하려고 같은 표기로 맞춥니다. */
const todayKey = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * 오늘보다 뒤인 날짜인가.
 * new Date(s) 파싱을 피하는 이유는 isPermanentDateValue 와 같습니다 — "2027-08-31" 은
 * UTC 자정으로 파싱돼 KST 로컬에서 하루 앞으로 밀립니다. 앞 10자리 문자열 비교면
 * 타임존과 무관하게 같은 답이 나옵니다.
 */
const isFutureDateValue = (v) =>
  v !== null && v !== undefined && String(v).slice(0, 10) > todayKey()

/**
 * is_superseded 가 아직 안 내려오는 서버(화면 선배포 구간)용 폴백.
 *
 * 그 구간에는 전환 행 자체가 없어서 사실상 항상 false 입니다 — 그래도 남겨 두는 이유는,
 * 백엔드 배포가 화면보다 늦어지는 사이에 만에 하나 전환 행이 생겼을 때 뱃지 없이 조용히
 * 지나가지 않게 하기 위함입니다. 폴백이라 가드가 필요합니다: 아래는 전부
 * "차감 근거가 될 수 없는 행"을 빼는 조건입니다(정책 §5 차감 대상 조건).
 *   무료 지급(F)   가치 0 이라 대체되지 않습니다
 *   테스트 결제     샌드박스가 실금액을 깎을 수 없습니다
 *   영구 / plan 없음 기간제 기능 이용권이 아닙니다
 * 서버가 값을 주기 시작하면 이 조건들은 서버가 이미 적용한 뒤라 쓰이지 않습니다.
 */
const isSupersededByShape = (row) =>
  row?.rental_status === 'EXPIRED' &&
  !isRefunded(row) &&
  !isFreeGift(row) &&
  !isTestRow(row) &&
  !hasNoPlan(row) &&
  !isPermanentRow(row) &&
  isFutureDateValue(row?.end_date)

/**
 * 상위 등급 전환으로 종료된 이용권 (정책 §5).
 *
 * 상위 등급을 살 때 하위 등급의 남은 가치를 청구액에서 깎아 주므로, 그 하위 이용권은
 * 그 시점에 끝납니다. 다만 서버는 **end_date 를 훼손하지 않습니다** — 상위 결제를 환불할 때
 * 원래 만료일로 되살려야 하기 때문입니다. 그래서 화면에는 `만료`인데 종료일은 미래인,
 * 그냥 보면 앞뒤가 안 맞는 행이 남습니다. 이 행을 "환불된 것"으로 오독하면 CS 안내가
 * 통째로 틀리므로(정책 §3) 그 자리에서 구분합니다.
 *
 * **판정은 is_superseded 로 합니다**(2026-08-31 백엔드 2차답변 §2·§4).
 * "EXPIRED + 종료일이 미래 ⟹ 전환" 은 지금 서버 CASE 문에서는 참이지만, 분기가 하나
 * 추가되면 조용히 깨지는 추론입니다. 서버가 값으로 내려주기로 해서 추론을 버립니다.
 * boolean 으로 좁혀 받는 이유는 isPermanentRow 와 같습니다 — 명시적 false 를 폴백이 뒤집으면
 * 안 됩니다. 특히 묶음(new) 구매 행은 도서가 살아 있어 ACTIVE + is_superseded=false 인데
 * (2차답변 §4-1) 그 행에 뱃지가 붙으면 "영구 도서가 종료됐다"는 정반대 오안내가 됩니다.
 */
export const isSupersededRow = (row) => {
  if (typeof row?.is_superseded === 'boolean') return row.is_superseded
  return isSupersededByShape(row)
}

/**
 * 이 결제의 전환을 일으킨 상위 결제의 pay_code (2차답변 §4).
 * 전환 종료 행에서 원인 결제로 곧장 넘어가는 데 씁니다 — 409 를 받을 때까지 기다리지 않아도 됩니다.
 */
export const supersededByPayCode = (row) =>
  isSupersededRow(row) ? row?.superseded_by_pay_code || '' : ''

export const SUPERSEDED_TEXT = '전환 종료'
export const SUPERSEDED_HINT =
  '상위 등급으로 전환되면서 종료된 이용권입니다. 환불이 아닙니다. ' +
  '종료일이 미래로 남아 있는 것은 상위 결제를 환불하면 이 이용권을 원래 만료일로 되살리기 때문입니다.'

/**
 * 환불 순서 가드 409 에서 "먼저 환불해야 할 상위 결제"의 pay_code 를 뽑습니다 (정책 §6-3).
 *
 * 안내 문장 자체는 그대로 보여주고, 이건 복사·이동 버튼에 쓸 값만 따로 꺼내는 용도입니다.
 * 못 뽑아도 안내는 그대로 나가야 하므로 실패는 빈 문자열입니다.
 */
export const refundOrderGuardPayCode = (detail) => {
  if (typeof detail !== 'string') return ''
  const m = detail.match(/pay_code\s*=\s*([A-Za-z0-9._-]+)/)
  // 문장 끝의 마침표까지 코드로 딸려오지 않게 잘라 냅니다.
  return m ? m[1].replace(/\.+$/, '') : ''
}

/**
 * 이 409 가 등급 전환 환불 순서 가드인가 (정책 §6-3).
 *
 * 같은 엔드포인트의 다른 409(이미 환불됨 · LS 결제가 아님 · 주문 식별자 없음)는 "닫고 끝"이지만
 * 이것만은 **다음에 할 일이 있는** 409 라 화면 처리가 달라야 합니다.
 *
 * pay_code 만으로는 판정하지 않습니다 — 다른 409 가 문구에 결제 코드를 싣기 시작하면
 * 그대로 같이 걸려서, 이미 환불된 건에 "아직 환불할 수 없다"는 안내가 붙습니다.
 * "순서" 를 말하는 낱말이 함께 있어야 이 가드로 봅니다.
 * 반대로 pay_code 를 못 뽑아도 문구만으로 알아볼 수 있게 둡니다 — 못 알아보면 토스트로
 * 흘러가 버려서, 놓치는 쪽이 잘못 잡는 쪽보다 훨씬 비쌉니다.
 */
export const isRefundOrderGuard = (detail) => {
  if (typeof detail !== 'string') return false
  const orderly = detail.includes('먼저 환불') || detail.includes('차감')
  return orderly && (!!refundOrderGuardPayCode(detail) || detail.includes('잔여 가치'))
}

/**
 * 화면에 그릴 업그레이드 차감액 (2차답변 §4).
 *
 * 차감 결제의 amount 는 정가보다 작은데, 이 값이 없으면 화면에서 "왜 이 결제만 싼가"를
 * 설명할 수단이 전혀 없어 결제 누락·부분 결제로 오인됩니다. 쿠폰과는 별개 개념이라
 * coupon_amount 에는 잡히지 않으므로 자리를 따로 잡습니다.
 *
 * 차감 없는 결제는 null 로 옵니다(미배포 서버는 undefined). 둘 다 0 으로 접어
 * 호출부에서 `> 0` 하나로 노출을 판정하게 합니다 — 0 을 "-0원"으로 그리지 않기 위함입니다.
 */
export const visibleCreditedAmount = (row) => Number(row?.credited_amount) || 0

export const CREDITED_HINT =
  '상위 등급으로 전환하면서 이미 쓰던 하위 등급의 남은 가치를 청구액에서 뺀 금액입니다. ' +
  '쿠폰 할인이 아니며, 그만큼 하위 이용권은 이 결제 시점에 종료됐습니다.'

/**
 * 묶음(new) 구매인데 기능 이용권만 끝난 행 (2차답변 §4-1).
 *
 * standard/premium 을 도서+기능 묶음으로 사고 나중에 상위로 전환하면, 그 묶음 결제 행은
 * `ACTIVE`(영구 도서는 살아 있음) + `is_superseded=false` + `feature_end_date=null` 이 됩니다.
 * 전환 종료 뱃지가 붙지 않는 것이 맞지만, 그러면 이 행만 봐서는 **기능이 어떻게 됐는지
 * 알 수 없습니다** — 기간 셀의 "기능 ~날짜" 부기가 조용히 사라질 뿐이라
 * "이 결제엔 기능이 없었다"로 읽힙니다.
 *
 * 만료인지 상위 전환인지는 이 응답만으로 구분되지 않으므로 **"기능 종료"까지만** 말합니다.
 * (구분이 필요하면 백엔드가 필드를 주기로 했습니다 — 2차답변 §4-1)
 *
 * === null 로 좁힌 이유: 미배포 서버는 feature_end_date 가 undefined 라 모든 std/prem 행에
 * 이 표시가 붙어 버립니다. 업그레이드 행을 빼는 이유는 그 행의 start~end 자체가 기능 기간이라
 * feature_end_date 를 따로 보지 않기 때문입니다.
 */
export const isFeatureEndedRow = (row) =>
  row?.feature_end_date === null &&
  row?.is_feature_permanent !== true &&
  (row?.grade === 'standard' || row?.grade === 'premium') &&
  !isUpgradeRow(row) &&
  !isRefunded(row) &&
  !isSupersededRow(row)

export const FEATURE_ENDED_TEXT = '기능 종료'
export const FEATURE_ENDED_HINT =
  '도서(영구)는 그대로 보유 중이고 이 결제에 딸려 있던 기능 이용권만 끝났습니다. ' +
  '만료됐거나 상위 등급으로 전환된 것인데, 결제 이력만으로는 둘을 구분할 수 없습니다.'

/* ────────────────────────────────────────────────────────────────
 * 요율 단위 통일 (docs/정산_프론트엔드개발_가이드.md §10.7)
 * ──────────────────────────────────────────────────────────────── */

/**
 * pg_fee_rate · sales_fee_rate 를 퍼센트 숫자로 맞춥니다.
 *
 * 2026-09-04 통지: 두 필드가 소수 배율(0.025)에서 퍼센트(2.5)로 바뀝니다. 요율의 크기와
 * 인세 금액은 그대로이고 표기 단위만 바뀝니다. 화면이 종전처럼 ×100 을 하면 백엔드 배포
 * 순간 2.5% 가 250% 로 찍힙니다.
 *
 * 배포 시점이 "별도 공지"라 화면과 순서를 맞출 수 없어, 통지가 준 과도기 판별식을 그대로
 * 씁니다(백엔드 마이그레이션도 같은 식을 씁니다). 실요율에 1% 이하가 없어 안전합니다.
 *   구단위 0.025 → 2.5 로 올리고, 신단위 2.5·20 은 그대로 통과.
 * 백엔드 배포가 끝나 소수 값이 더 이상 오지 않으면 이 함수는 항등식이 됩니다.
 *
 * ⚠ 환율(exchange_rate)에는 쓰지 마세요 — 이름에 rate 가 들어가도 배율이라 무관합니다.
 *
 * 값이 없을 때 0 으로 접지 않는 이유: 안 내려온 요율을 "0%" 로 그리면 수수료를 안 뗀
 * 결제로 읽힙니다. 종전과 같이 NaN 을 그대로 넘겨 호출부가 판단하게 둡니다.
 */
export const feeRatePercent = (v) => {
  const n = Number(v)
  return n > 0 && n <= 1 ? n * 100 : n
}

/* ────────────────────────────────────────────────────────────────
 * 환불 14일 기한 (docs/환불_즉시반영_관리자화면_가이드.md §3.3 "환불 기한 14일")
 * ──────────────────────────────────────────────────────────────── */

/** 이용약관의 환불 기한. 결제일(KST 날짜) + 이 일수의 자정까지 환불할 수 있습니다. */
export const REFUND_WINDOW_DAYS = 14

/**
 * 오늘(KST) YYYY-MM-DD.
 *
 * todayKey 와 달리 브라우저 로컬이 아니라 **Asia/Seoul 고정**입니다. 서버 판정이 KST 날짜라
 * (docs/환불_즉시반영_관리자화면_가이드.md §3.3 판정식), 해외에서 접속한 관리자의 로컬 날짜로 비교하면 서버와 하루가 어긋나
 * 버튼은 활성인데 409 가 오거나 그 반대가 됩니다. LemonSqueezy(해외 결제)도 같은 KST 기준입니다.
 * en-CA 로케일은 ISO 순서(YYYY-MM-DD)로 찍어 주므로 문자열 비교에 그대로 씁니다.
 * Intl 을 못 쓰는 환경에서만 로컬 날짜로 물러납니다.
 */
const todayKstKey = () => {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date())
  } catch (e) {
    return todayKey()
  }
}

/**
 * 이 결제의 환불 기한 YYYY-MM-DD (결제일 + 14일). 이 날짜까지 환불할 수 있습니다.
 *
 * payment_date 는 시간대 표기 없는 KST 저장값 그대로 옵니다("2026-08-18T18:58:36",
 * docs/결제_영구소유_개편_관리자화면_가이드.md §3.2-1). new Date() 로 파싱하면 브라우저 로컬로 해석돼 날짜가 밀릴 수 있어
 * isFutureDateValue 와 같은 이유로 앞 10자리를 문자열로 뜯고, 덧셈만 UTC 산술로 합니다
 * (월말·윤년을 직접 계산하지 않기 위해). 시·분은 보지 않습니다 — 서버도 날짜만 비교합니다.
 *
 * 결제일을 읽을 수 없으면 빈 문자열입니다. 호출부는 이를 "판정 불가"로 보고 버튼을 잠그지
 * 않습니다 — 없는 정보로 환불을 막지 않고 서버 판정에 맡깁니다.
 */
export const refundDeadlineKey = (row) => {
  const m = String(row?.payment_date ?? '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!m) return ''
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + REFUND_WINDOW_DAYS)
  return new Date(t).toISOString().slice(0, 10)
}

/**
 * 환불 가능 기간이 지났는가 — 통지의 판정식 `date(payment_date) + 14 >= today(KST)` 의 부정.
 *
 * 이 판정은 **버튼을 미리 잠가 불필요한 409 를 줄이는 용도**이고 정본은 서버입니다.
 * 자정 경계(목록을 띄운 뒤 날짜가 바뀜)나 시계 오차로 버튼이 활성인 채 409 가 올 수 있으며,
 * 그때는 서버 detail 을 그대로 보여줍니다. 반대로 여기서 잠긴 건을 풀어 주는 우회 수단은
 * 두지 않습니다 — 관리자 재량 예외가 없다는 것이 통지의 핵심입니다.
 *
 * canRefundExec 에 섞지 않는 이유: 그 함수는 "이 화면에서 환불하는 결제수단인가"(T/L)라
 * 버튼을 **감추는** 조건이고, 기한 경과는 버튼을 **보이되 잠그는** 조건입니다(docs/환불_즉시반영_관리자화면_가이드.md §3.3).
 * 섞으면 기한이 지난 T/L 행이 A/G 처럼 "스토어에서 직접 환불" 로 오안내됩니다.
 */
export const isRefundWindowClosed = (row) => {
  const deadline = refundDeadlineKey(row)
  return !!deadline && deadline < todayKstKey()
}

/** 잠긴 버튼의 툴팁 문구. 통지가 제안한 형식 그대로입니다. */
export const refundWindowClosedText = (row) =>
  `환불 가능 기간 경과 (기한 ${refundDeadlineKey(row)})`
